import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'

export interface Contact {
  id: string
  name: string
  phone: string
  email?: string
  campaignId?: string
}

export interface CallMetrics {
  mos: number
  jitter: number
  latency: number
}

export type CallStatus = 'idle' | 'connecting' | 'ringing' | 'active' | 'disconnected'

interface CallState {
  // État de l'appel
  isInCall: boolean
  callId: string | null
  contact: Contact | null
  phoneNumber: string | null
  
  // État Twilio
  callStatus: CallStatus
  isMuted: boolean
  isOnHold: boolean
  duration: number
  startTime: Date | null
  
  // Métriques
  callMetrics: CallMetrics
  
  // Campaign
  campaignName: string | null
  
  // Actions
  startCall: (callId: string, contact: Contact, phoneNumber: string, campaignName?: string) => void
  endCall: () => void
  updateCallStatus: (status: CallStatus) => void
  setMuted: (muted: boolean) => void
  setOnHold: (hold: boolean) => void
  updateMetrics: (metrics: Partial<CallMetrics>) => void
  updateContact: (contact: Contact) => void
}

const CallStateContext = createContext<CallState | undefined>(undefined)

export function CallStateProvider({ children }: { children: ReactNode }) {
  const [isInCall, setIsInCall] = useState(false)
  const [callId, setCallId] = useState<string | null>(null)
  const [contact, setContact] = useState<Contact | null>(null)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
  const [callStatus, setCallStatus] = useState<CallStatus>('idle')
  const [isMuted, setIsMuted] = useState(false)
  const [isOnHold, setIsOnHold] = useState(false)
  const [duration, setDuration] = useState(0)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [callMetrics, setCallMetrics] = useState<CallMetrics>({
    mos: 0,
    jitter: 0,
    latency: 0,
  })
  const [campaignName, setCampaignName] = useState<string | null>(null)

  // Timer pour incrémenter la durée
  useEffect(() => {
    if (!isInCall || callStatus !== 'active') return

    const interval = setInterval(() => {
      setDuration(prev => prev + 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [isInCall, callStatus])

  const startCall = useCallback((
    newCallId: string,
    newContact: Contact,
    newPhoneNumber: string,
    newCampaignName?: string
  ) => {
    console.log('🎯 CallStateContext: Starting call', { callId: newCallId, contact: newContact.name })
    setIsInCall(true)
    setCallId(newCallId)
    setContact(newContact)
    setPhoneNumber(newPhoneNumber)
    setCallStatus('connecting')
    setDuration(0)
    setStartTime(new Date())
    setIsMuted(false)
    setIsOnHold(false)
    setCampaignName(newCampaignName || null)
    setCallMetrics({ mos: 0, jitter: 0, latency: 0 })
  }, [])

  const endCall = useCallback(() => {
    console.log('🎯 CallStateContext: Ending call')
    setIsInCall(false)
    setCallId(null)
    setContact(null)
    setPhoneNumber(null)
    setCallStatus('disconnected')
    setDuration(0)
    setStartTime(null)
    setIsMuted(false)
    setIsOnHold(false)
    setCampaignName(null)
    setCallMetrics({ mos: 0, jitter: 0, latency: 0 })
    
    // Reset to idle after a short delay
    setTimeout(() => {
      setCallStatus('idle')
    }, 1000)
  }, [])

  const updateCallStatus = useCallback((status: CallStatus) => {
    console.log('🎯 CallStateContext: Updating status', status)
    setCallStatus(status)
  }, [])

  const updateMetrics = useCallback((metrics: Partial<CallMetrics>) => {
    setCallMetrics(prev => ({ ...prev, ...metrics }))
  }, [])

  const updateContact = useCallback((newContact: Contact) => {
    setContact(newContact)
  }, [])

  const value: CallState = {
    isInCall,
    callId,
    contact,
    phoneNumber,
    callStatus,
    isMuted,
    isOnHold,
    duration,
    startTime,
    callMetrics,
    campaignName,
    startCall,
    endCall,
    updateCallStatus,
    setMuted: setIsMuted,
    setOnHold: setIsOnHold,
    updateMetrics,
    updateContact,
  }

  return (
    <CallStateContext.Provider value={value}>
      {children}
    </CallStateContext.Provider>
  )
}

export function useCallState() {
  const context = useContext(CallStateContext)
  if (context === undefined) {
    throw new Error('useCallState must be used within a CallStateProvider')
  }
  return context
}
